/**
 * Connector catalogue. Every connector declares what it can do; the product
 * never assumes a capability that is not declared here *and* granted on the
 * actual connection (e.g. Meta permissions).
 */

export const CONNECTOR_TYPES = [
  'facebook_messenger',
  'instagram',
  'website_form',
  'email',
  'meta_lead_ads',
  'google_lead_forms',
  'whatsapp',
  'telegram',
  'reddit',
] as const;
export type ConnectorType = (typeof CONNECTOR_TYPES)[number];

/** Where a lead came from. Includes non-connector sources. */
export const LEAD_SOURCES = [...CONNECTOR_TYPES, 'manual', 'csv_import'] as const;
export type LeadSource = (typeof LEAD_SOURCES)[number];

export interface ConnectorCapabilities {
  inboundMessages: boolean;
  leadForms: boolean;
  historicalImport: boolean;
  outgoingReplies: boolean;
  webhooks: boolean;
  polling: boolean;
  deliveryStatus: boolean;
  mediaAttachments: boolean;
}

export type ConnectorAvailability = 'available' | 'planned';

/** Server-side feature flags (env FEATURE_*) that gate Phase 2 connectors. */
export const FEATURE_FLAGS = ['googleLeadForms', 'metaLeadAds', 'whatsapp', 'whatsappEmbeddedSignup', 'smartOperations'] as const;
export type FeatureFlag = (typeof FEATURE_FLAGS)[number];

/** How the Channels page groups connectors. */
export type ConnectorGroup = 'messaging' | 'lead_forms' | 'other';

export interface ConnectorDefinition {
  type: ConnectorType;
  label: string;
  description: string;
  availability: ConnectorAvailability;
  /** Two-way messaging thread vs one-time submissions. */
  interaction: 'conversation' | 'submission';
  capabilities: ConnectorCapabilities;
  group: ConnectorGroup;
  /** Available only when this server enables the flag; otherwise shown as planned. */
  featureFlag?: FeatureFlag;
  /** Shown when availability is "planned" or setup has prerequisites. */
  note?: string;
}

const none: ConnectorCapabilities = {
  inboundMessages: false,
  leadForms: false,
  historicalImport: false,
  outgoingReplies: false,
  webhooks: false,
  polling: false,
  deliveryStatus: false,
  mediaAttachments: false,
};

export const CONNECTORS: Record<ConnectorType, ConnectorDefinition> = {
  facebook_messenger: {
    type: 'facebook_messenger',
    group: 'messaging',
    label: 'Facebook Messenger',
    description: 'Messages sent to your Facebook Pages.',
    availability: 'available',
    interaction: 'conversation',
    capabilities: {
      ...none,
      inboundMessages: true,
      historicalImport: true,
      outgoingReplies: true,
      webhooks: true,
      deliveryStatus: true,
      mediaAttachments: true,
    },
    note: 'Uses Facebook Login for Business. Replies are limited to the 24-hour messaging window.',
  },
  instagram: {
    type: 'instagram',
    group: 'messaging',
    label: 'Instagram',
    description: 'Direct messages to an Instagram professional account.',
    availability: 'available',
    interaction: 'conversation',
    capabilities: {
      ...none,
      inboundMessages: true,
      historicalImport: true,
      outgoingReplies: true,
      webhooks: true,
      mediaAttachments: true,
    },
    note: 'Uses Instagram API with Instagram Login. Replies are limited to the 24-hour messaging window.',
  },
  website_form: {
    type: 'website_form',
    group: 'lead_forms',
    label: 'Website form',
    description: 'A hosted or embeddable contact form, plus a secure server-to-server API.',
    availability: 'available',
    interaction: 'submission',
    capabilities: { ...none, leadForms: true, webhooks: true },
  },
  email: {
    type: 'email',
    group: 'other',
    label: 'Business email',
    description: 'Receive inquiries by forwarding email, and reply from your own mailbox via SMTP.',
    availability: 'available',
    interaction: 'conversation',
    capabilities: { ...none, inboundMessages: true, outgoingReplies: true, webhooks: true },
    note: 'Inbound mail arrives via a forwarding service that posts raw MIME to Knownlead.',
  },
  meta_lead_ads: {
    type: 'meta_lead_ads',
    group: 'lead_forms',
    label: 'Meta lead ads',
    description: 'Instant Form leads from your Facebook and Instagram ads.',
    availability: 'available',
    featureFlag: 'metaLeadAds',
    interaction: 'submission',
    capabilities: { ...none, leadForms: true, webhooks: true, historicalImport: true },
    note: 'Connect a Facebook Page you can advertise for, then choose its Instant Forms. Replies go by email or phone — Meta has no reply API for lead forms.',
  },
  google_lead_forms: {
    type: 'google_lead_forms',
    group: 'lead_forms',
    label: 'Google Ads lead forms',
    description: 'Leads from lead form assets in your Google Ads campaigns, delivered by webhook.',
    availability: 'available',
    featureFlag: 'googleLeadForms',
    interaction: 'submission',
    capabilities: { ...none, leadForms: true, webhooks: true },
    note: 'You paste a webhook URL and key into each lead form in Google Ads. Replies go by email or phone — Google has no reply API.',
  },
  whatsapp: {
    type: 'whatsapp',
    group: 'messaging',
    label: 'WhatsApp Business',
    description: 'Messages to your WhatsApp business number, through the official Cloud API.',
    availability: 'available',
    featureFlag: 'whatsapp',
    interaction: 'conversation',
    capabilities: { ...none, inboundMessages: true, outgoingReplies: true, webhooks: true, deliveryStatus: true, mediaAttachments: true },
    note: 'Free-form replies within 24 hours of the customer’s last message; after that only approved templates, which Meta charges for.',
  },
  telegram: {
    type: 'telegram',
    group: 'messaging',
    label: 'Telegram',
    description: 'Messages sent to your Telegram bot.',
    availability: 'planned',
    interaction: 'conversation',
    capabilities: { ...none, inboundMessages: true, outgoingReplies: true, webhooks: true },
    note: 'Planned: Telegram Bot API. Bots only receive messages users send to the bot.',
  },
  reddit: {
    type: 'reddit',
    group: 'other',
    label: 'Reddit',
    description: 'Not supported as an inquiry source.',
    availability: 'planned',
    interaction: 'conversation',
    capabilities: none,
    note: 'No approved API for reading a business’s private messages as leads. Use email forwarding or manual entry.',
  },
};

export const CONNECTION_STATUSES = [
  'connected',
  'setup_required',
  'permission_required',
  'syncing',
  'degraded',
  'expired',
  'disconnected',
  'unsupported',
] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number];

export const CONNECTION_STATUS_LABELS: Record<ConnectionStatus, string> = {
  connected: 'Connected',
  setup_required: 'Setup required',
  permission_required: 'Permission required',
  syncing: 'Syncing',
  degraded: 'Degraded',
  expired: 'Expired',
  disconnected: 'Disconnected',
  unsupported: 'Unsupported',
};

export const SOURCE_LABELS: Record<LeadSource, string> = {
  ...Object.fromEntries(CONNECTOR_TYPES.map((t) => [t, CONNECTORS[t].label])),
  manual: 'Manual entry',
  csv_import: 'CSV import',
} as Record<LeadSource, string>;

/** Meta permissions each Meta connector needs for its features. */
export const META_REQUIRED_PERMISSIONS = {
  facebook_messenger: {
    required: ['pages_show_list', 'pages_messaging', 'pages_manage_metadata'],
    /** Conversations API (history import) additionally needs pages_read_engagement. */
    historyImport: ['pages_messaging', 'pages_manage_metadata', 'pages_read_engagement'],
  },
  instagram: {
    required: ['instagram_business_basic', 'instagram_business_manage_messages'],
    historyImport: ['instagram_business_basic', 'instagram_business_manage_messages'],
  },
  /**
   * Page leadgen webhook + reading leads with a Page token. Meta's permission
   * reference also lists ads_management, ads_read and business_management as
   * App Review dependencies of leads_retrieval; Knownlead calls no endpoint that
   * needs them, so they are not checked here (see docs/PHASE2_APP_REVIEW.md).
   */
  meta_lead_ads: {
    required: ['pages_show_list', 'pages_read_engagement', 'pages_manage_metadata', 'pages_manage_ads', 'leads_retrieval'],
    historyImport: ['leads_retrieval', 'pages_manage_ads'],
  },
  /** Cloud API: messaging needs whatsapp_business_messaging, which depends on whatsapp_business_management. */
  whatsapp: {
    required: ['whatsapp_business_management', 'whatsapp_business_messaging'],
    historyImport: [],
  },
} as const;

export const INSTAGRAM_LOGIN_SCOPES = [
  'instagram_business_basic',
  'instagram_business_manage_messages',
] as const;

export const MESSAGING_WINDOW_HOURS = 24;

const ATTACHMENT_LABELS: Record<string, string> = {
  image: 'Sent a photo',
  video: 'Sent a video',
  audio: 'Sent a voice message',
  file: 'Sent a file',
  sticker: 'Sent a sticker',
  location: 'Shared a location',
  share: 'Shared a post',
  ig_reel: 'Shared a reel',
  reel: 'Shared a reel',
  story_reply: 'Replied to your story',
  story_mention: 'Mentioned you in their story',
  unsupported: "Sent a message type that Meta doesn't share with apps",
};

/** Human description of a message that has no text, e.g. "Shared a reel". */
export function attachmentLabel(attachments: { type?: string | null }[] | null | undefined): string {
  const type = attachments?.[0]?.type;
  if (!type) return 'Sent a message';
  return ATTACHMENT_LABELS[type] ?? (type.startsWith('image/') ? 'Sent a photo' : type.startsWith('video/') ? 'Sent a video' : 'Sent an attachment');
}

/** How a reply to a given inquiry can be delivered. */
export type ResponseMode = 'api' | 'email' | 'open_original' | 'manual';

export const RESPONSE_MODE_LABELS: Record<ResponseMode, string> = {
  api: 'Reply in Knownlead',
  email: 'Reply by email',
  open_original: 'Open original conversation',
  manual: 'Contact outside Knownlead',
};

/** Whether a connector can be set up on this server (catalogue availability + feature flag). */
export function connectorEnabled(def: ConnectorDefinition, features: Partial<Record<FeatureFlag, boolean>> | undefined): boolean {
  if (def.availability !== 'available') return false;
  return !def.featureFlag || !!features?.[def.featureFlag];
}
